<template>
    <cr-app :style="themeStyle" :class="appClasses">
        <splash-screen v-if="showSplashScreen" />
        <router-view :key="fullPath"></router-view>
        <theme-editor v-if="showThemeEditor" class="theme-editor" />
    </cr-app>
</template>

<style>
* {
    --themed-accessible-text-shadow:
        -1px -1px 0 var(--cr-background-image-color),
         1px -1px 0 var(--cr-background-image-color),
        -1px  1px 0 var(--cr-background-image-color),
         1px  1px 0 var(--cr-background-image-color); 
}

.accessible-text-shadow {
    text-shadow: var(--themed-accessible-text-shadow)
}
</style>


<style lang="less">
@font-face { 
    font-family: "IBM Plex Mono";
    src: url("../assets/themes/default/IBMPlexMono-Medium.ttf"); 
    font-weight: 400;
}
@font-face { 
    font-family: "IBM Plex Mono";
    src: url("../assets/themes/default/IBMPlexMono-Bold.ttf"); 
    font-weight: 700;
}

@font-face { 
    font-family: "Blinker";
    src: url("../assets/themes/dark/Blinker-SemiBold.ttf"); 
    font-weight: 400;
}
@font-face { 
    font-family: "Blinker";
    src: url("../assets/themes/dark/Blinker-Bold.ttf"); 
    font-weight: 500;
}

@font-face { 
    font-family: "Bangers-Regular";
    src: url("../assets/themes/comicBook/Bangers-Regular.ttf");
}

@font-face { 
    font-family: "ComicNeue";
    src: url("../assets/themes/comicBook/ComicNeue-Regular.ttf");
    font-weight: 400;
}
@font-face { 
    font-family: "ComicNeue";
    src: url("../assets/themes/comicBook/ComicNeue-Bold.ttf");
    font-weight: 700;
}

@font-face {
    font-family: "ShareTech";
    src: url("../assets/themes/cyberIcons/ShareTech-Regular.ttf");
}
@font-face {
    font-family: "ShareTechMono";
    src: url("../assets/themes/cyberIcons/ShareTechMono-Regular.ttf");
}
@font-face {
    font-family: "WDXL-Lubrifont-SC";
    src: url("../assets/themes/winter/WDXLLubrifontSC-Regular.ttf");
}
@font-face {
    font-family: "Inter";
    src: url("../assets/themes/crossland/Inter-Variable.ttf");
}
@font-face {
    font-family: "Jersey15";
    src: url("../assets/themes/loveBytes/Jersey15-Regular.ttf");
}
</style>

<style scoped>
.theme-editor-wrapper {
    width: calc(100% - 20rem);
}
.theme-editor {
  background: white;
  color: black;
  padding: 1rem;
  width: 20rem;
  color-scheme: only light;
  z-index: 1500; /** appear in front of dialog backdrops and behind tooltips */
  height: 100vh;
  overflow-y: auto;
  position: fixed;
  top: 0;
  right: 0;
}
.theme-editor ~ :deep(.cr-dialog) {
    width: inherit;
}
</style>

<script lang="ts" setup>
import '@/styles/themedFocusIndicators.css';
import '@/styles/allInputSpacing.css';
import '@/styles/dialogs.css';
import { storeToRefs } from 'pinia';
import SplashScreen from '@components/SplashScreen.vue';
import { computed } from "vue";
import { useSplashScreenStore } from "@/stores/splashScreenStore";
import { useThemeStore } from '@/stores/themeStore';
import { useRoute } from 'vue-router';

const route = useRoute();
const fullPath = computed(()=> 
{
    return `${route?.fullPath}_${route?.meta?.navigationId}`;
});

const { currentTheme, showThemeEditor } = storeToRefs(useThemeStore());
const { showSplashScreen } = storeToRefs(useSplashScreenStore());

const appClasses = computed(() =>
{
    return {
        [currentTheme.value.id.toLowerCase()]:true,
        'theme-editor-wrapper': showThemeEditor.value,
    }
})

const themeStyle = computed(() =>
{
    let style = {};

    if(currentTheme.value.colors.application)
    {
        style['background'] = currentTheme.value.colors.application;
    }

    if(currentTheme.value.colors.text)
    {
        style['color'] = `${currentTheme.value.colors.text} !important`;
    }

    if(currentTheme.value.backgroundImage)
    {
        style['background-image'] = `url(${currentTheme.value.backgroundImage})`;
        style['background-size'] = 'cover';
        style['background-repeat'] = 'no-repeat';
        style['background-position'] = 'top left';
        style['background-attachment'] = 'fixed';
    }

    if(currentTheme.value.fontFamily)
    {
        style['font-family'] = `${currentTheme.value.fontFamily} !important`; 
    }

    if(currentTheme.value.letterSpacing)
    {
        style['letter-spacing'] = `${currentTheme.value.letterSpacing} !important`
    }

    style['color-scheme'] = currentTheme.value.isDark ? 'only dark' : 'only light';

    return style;
});
</script>
